import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { Spinner } from "react-bootstrap";
import { PostRequest } from "../../utils/ApiServices";
import { GlobalContext } from "../../context/GlobalState";
import i18n from "../../i18n/i18n";

const countryCodes = [
  { code: "+971", label: "UAE (+971)" },
  { code: "+966", label: "KSA (+966)" },
  { code: "+965", label: "Kuwait (+965)" },
  { code: "+968", label: "Oman (+968)" },
  { code: "+974", label: "Qatar (+974)" },
  { code: "+973", label: "Bahrain (+973)" },
  { code: "+91", label: "India (+91)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+1", label: "US (+1)" },
];

function TabbyPayment() {
  const navigate = useNavigate();
  const [myJwtCookie] = useCookies(["jwt"]);
  const { userState } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pre-fill from GlobalContext / Profile
  const user = userState.user;
  const [fullName, setFullName] = useState(`${user?.firstname || ""} ${user?.lastname || ""}`.trim());
  const [email, setEmail] = useState("otp.success@tabby.ai");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+971");

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
    
    if (!user) {
      navigate("/login");
      return;
    }

    // Sync state if user loads after mount
    if (user.firstname && !fullName) {
      setFullName(`${user.firstname} ${user.lastname || ""}`.trim());
    }
  }, [user, navigate, fullName]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName || fullName.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }

    if (!email || !email.includes("@") || !email.includes(".")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!phoneNumber || phoneNumber.length < 7) {
      setError("Please enter a valid phone number.");
      return;
    }

    setError(null);

    try {
      setIsLoading(true);

      const cart = JSON.parse(localStorage.getItem("cart")) || [];
      const sum = cart.reduce((acc, curr) => {
        let price = curr.price;
        return acc + price * (curr?.enrolment_quantity || 1);
      }, 0);
      const amount = sum + sum * 0.05;
      const promocode = localStorage.getItem("promoCode") ?? null;

      const fullPhone = `${countryCode}${phoneNumber}`;

      // Filter the cart to only send the fields the backend expects
      const filteredCourses = cart.map((course) => ({
        id: course.id,
        course_name: course.course_name,
        price: course.price,
        enrolment_quantity: course.enrolment_quantity || 1
      }));

      const postData = {
        amount: amount.toFixed(2),
        courses: filteredCourses,
        customer_id: user.id,
        code: promocode,
        payment_gateway: "tabby",
        buyer: {
          phone: fullPhone,
          email: email.trim(),
          name: fullName.trim(),
        },
        success_url: `${window.location.origin}/payment-successful`,
        cancel_url: `${window.location.origin}/payment-failed`,
        failure_url: `${window.location.origin}/payment-failed`,
        // Added redundant aliases to ensure backend compatibility
        return_url: `${window.location.origin}/payment-successful`,
        redirect_url: `${window.location.origin}/payment-successful`,
      };

      let requiredFields = null;
      try {
        requiredFields = JSON.parse(localStorage.getItem("requiredFields"));
      } catch {
        console.log("error while parsing requiredFields");
      }

      if (requiredFields) {
        postData.requiredFields = requiredFields;
      }

      console.log("Tabby Checkout - Sending postData:", JSON.stringify(postData, null, 2));

      const response = await PostRequest("gotocheckout", postData);

      if (response && response.status === "success" && (response.web_url || response.redirect_url)) {
        window.location.href = response.web_url || response.redirect_url;
      } else {
        setError(response?.message || "Failed to initialize Tabby session.");
      }
    } catch (err) {
      console.error("Tabby Checkout Error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tabby-payment-container">
      <style>{`
        .tabby-payment-container {
          padding: 160px 20px 80px;
          display: flex;
          justify-content: center;
          background: #f8f9fa;
          min-height: 100vh;
        }
        .tabby-card {
          background: #fff;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          width: 100%;
          max-width: 500px;
          text-align: center;
        }
        .tabby-title {
          font-size: 28px;
          font-weight: 700;
          color: #2b313b;
          margin-bottom: 5px;
        }
        .tabby-subtitle {
          color: #666;
          font-size: 15px;
          margin-bottom: 30px;
        }
        .tabby-form-group {
          margin-bottom: 20px;
          text-align: left;
        }
        .tabby-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #4a4a4a;
          margin-bottom: 8px;
          margin-left: 5px;
        }
        .tabby-input {
          width: 100%;
          padding: 12px 15px;
          border: 1.5px solid #eee;
          border-radius: 10px;
          font-size: 15px;
          transition: border-color 0.2s;
        }
        .tabby-input:focus {
          outline: none;
          border-color: #36D5C7;
        }
        .tabby-phone-wrapper {
          display: flex;
          gap: 10px;
        }
        .tabby-country-select {
          width: 130px;
          background: #fff;
          border: 1.5px solid #eee;
          border-radius: 10px;
          padding: 0 10px;
          font-size: 14px;
        }
        .tabby-btn {
          width: 100%;
          padding: 14px;
          border-radius: 10px;
          border: none;
          background: #36D5C7;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 10px;
        }
        .tabby-btn:hover {
          background: #2bc4b6;
        }
        .tabby-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .tabby-error {
          background: #fff0f0;
          color: #e74c3c;
          padding: 10px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 20px;
          border: 1px solid #ffdbdb;
        }
        .tabby-back {
          margin-top: 20px;
          background: none;
          border: none;
          color: #999;
          font-size: 14px;
          text-decoration: underline;
          cursor: pointer;
        }
      `}</style>

      <div className="tabby-card">
        <h2 className="tabby-title">Update details to proceed</h2>
        <p className="tabby-subtitle">
          Please provide your contact information to continue with the payment.
        </p>

        {error && <div className="tabby-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="tabby-form-group">
            <label className="tabby-label">Full Name</label>
            <input
              type="text"
              className="tabby-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
            />
          </div>

          <div className="tabby-form-group">
            <label className="tabby-label">Email Address</label>
            <input
              type="email"
              className="tabby-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>

          <div className="tabby-form-group">
            <label className="tabby-label">Mobile Number</label>
            <div className="tabby-phone-wrapper">
              <select
                className="tabby-country-select"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="tabby-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="5XXXXXXXX"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-hover-dark w-100" 
            style={{ padding: "14px", fontSize: "16px", borderRadius: "10px", marginTop: "10px" }}
            disabled={isLoading}
          >
            {isLoading ? (
              <><Spinner animation="border" size="sm" /> Processing...</>
            ) : (
              "Continue to Payment"
            )}
          </button>
        </form>

        <button className="tabby-back" onClick={() => navigate("/payment-selection")}>
          ← Back to Payment Selection
        </button>
      </div>
    </div>
  );
}

export default TabbyPayment;
